/**
 * Microsoft Entra ID SSO OAuth service for authentication
 * Implements OAuth 2.0 authorization code flow
 */
import axios, { isAxiosError } from 'axios';

import { AuthResponse } from '../types/auth';
import { logger } from '../utils/logger';

interface AuthConfig {
  entra_id_client_id: string;
  entra_id_authorization_server: string;
  entra_id_scope: string;
}

interface AuthStartResponse {
  authorization_url: string;
}

/**
 * Hosts allowed to serve the OAuth authorization page. The redirect origin is
 * always taken from this constant list and never from the backend response,
 * so a tampered response cannot cause an open redirect
 * (SonarQube tssecurity:S6105).
 */
const TRUSTED_AUTHORIZATION_HOSTS = [
  'login.microsoftonline.com',
  'login.microsoftonline.us',
  'login.partner.microsoftonline.cn',
];

/** Scheme required for the authorization endpoint. */
const HTTPS_PROTOCOL = 'https:';

/** Maximum accepted length of an authorization URL. */
const MAX_AUTHORIZATION_URL_LENGTH = 2048;

/** Path separator that a valid relative authorization target starts with. */
const PATH_SEPARATOR = '/';

/** Characters permitted in an authorization URL. */
const ALLOWED_URL_CHARACTERS = [
  'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
  'abcdefghijklmnopqrstuvwxyz',
  '0123456789',
  "-._~:/?#[]@!$&'()*+,;=%",
].join('');

/**
 * Rebuild a value character by character using only characters read from the
 * constant `ALLOWED_URL_CHARACTERS` allow-list.
 *
 * @param value - Value to rebuild.
 * @returns The rebuilt value, or `null` when it is empty, too long or holds a
 *          character outside the allow-list.
 */
const rebuildFromAllowedCharacters = (value: string): string | null => {
  if (value.length === 0 || value.length > MAX_AUTHORIZATION_URL_LENGTH) {
    return null;
  }

  let rebuilt = '';
  for (let index = 0; index < value.length; index += 1) {
    const position = ALLOWED_URL_CHARACTERS.indexOf(value.charAt(index));
    if (position === -1) {
      return null;
    }
    rebuilt += ALLOWED_URL_CHARACTERS.charAt(position);
  }

  return rebuilt;
};

/**
 * Build the browser navigation target for the OAuth authorization page.
 *
 * The origin is selected by index from `TRUSTED_AUTHORIZATION_HOSTS` and the
 * path/query are rebuilt from the constant character allow-list, so the
 * returned string is composed entirely of constant-derived characters.
 *
 * @param candidate - Authorization URL reported by the backend.
 * @returns A safe absolute URL, or `null` when the candidate is not trusted.
 */
const buildTrustedAuthorizationUrl = (candidate: string): string | null => {
  const normalizedUrl = rebuildFromAllowedCharacters(candidate);
  if (!normalizedUrl) {
    return null;
  }

  let parsed: URL;
  try {
    parsed = new URL(normalizedUrl);
  } catch {
    return null;
  }

  const hostIndex = TRUSTED_AUTHORIZATION_HOSTS.indexOf(parsed.hostname);
  if (parsed.protocol !== HTTPS_PROTOCOL || hostIndex === -1) {
    return null;
  }

  const relativeTarget = rebuildFromAllowedCharacters(
    `${parsed.pathname}${parsed.search}`,
  );
  if (!relativeTarget?.startsWith(PATH_SEPARATOR)) {
    return null;
  }

  const trustedHost = TRUSTED_AUTHORIZATION_HOSTS[hostIndex];
  return `${HTTPS_PROTOCOL}//${trustedHost}${relativeTarget}`;
};

class EntraIDAuthService {
  private clientId: string;
  private readonly apiBaseUrl: string;
  private readonly redirectUri: string;
  private configLoaded: boolean = false;

  constructor() {
    // Initialize with fallback values (will be overridden by backend config)
    this.clientId = '';
    this.apiBaseUrl = import.meta.env.REACT_APP_API_URL || 'http://localhost:8000';
    this.redirectUri = `${globalThis.location.origin}/auth/callback`;
  }

  /**
   * Fetch OAuth configuration from backend
   * Single source of truth - no more duplicate ENTRA_ID_CLIENT_ID env vars!
   */
  private async loadConfig(): Promise<void> {
    if (this.configLoaded) {
      return;
    }

    try {
      const response = await axios.get<AuthConfig>(`${this.apiBaseUrl}/auth/config`);
      this.clientId = response.data.entra_id_client_id;
      this.configLoaded = true;
    } catch (error) {
      logger.error('Failed to load auth config from backend:', error);
      throw new Error('Authentication configuration unavailable');
    }
  }

  /**
   * Redirect to Entra ID OAuth authorization.
   * For confidential clients (Web apps with secret), we don't use PKCE.
   * The backend uses client_secret for authentication.
   */
  public async initiateLogin(): Promise<void> {
    const response = await axios.get<AuthStartResponse>(
      `${this.apiBaseUrl}/auth/entra-id/start`,
      {
        params: {
          redirect_uri: this.redirectUri,
        },
      },
    );

    const safeAuthorizationUrl = buildTrustedAuthorizationUrl(
      response.data.authorization_url ?? '',
    );
    if (!safeAuthorizationUrl) {
      throw new Error('Authentication configuration unavailable');
    }

    globalThis.location.assign(safeAuthorizationUrl);
  }

  /**
   * Handle OAuth callback and exchange code for token.
   * For confidential clients (Web apps), PKCE is not used.
   */
  public async handleCallback(code: string, state: string): Promise<AuthResponse> {
    try {
      // Exchange OAuth code for access token
      // Note: For confidential clients (Web apps), we don't send code_verifier
      // The backend uses client_secret instead
      const form = new URLSearchParams();
      form.set('code', code);
      form.set('state', state);
      form.set('redirect_uri', this.redirectUri);
      // Don't set code_verifier for confidential clients
      
      const response = await axios.post(
        `${this.apiBaseUrl}/auth/entra-id`,
        form,
        {
          // The backend exchanges the code and resolves group memberships via
          // upstream Microsoft calls, which can take well over 10s. Allow ample
          // headroom so a slow-but-successful login isn't reported as a failure.
          timeout: 30000, // 30 second timeout
          withCredentials: false,
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          validateStatus: (s) => s < 500,
        },
      );
      
      if (response.status !== 200) {
        throw new Error(response.data?.detail || 'Authentication failed');
      }
      
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        const message = error.response?.data?.detail || 'Authentication failed';
        throw new Error(message);
      }
      // If it's already an Error with a message (thrown from line 126), preserve it
      if (error instanceof Error && error.message) {
        throw error;
      }
      throw new Error('Network error during authentication');
    }
  }

  /**
   * Get current user info using stored token
   */
  public async getCurrentUser(token: string) {
    try {
      const response = await axios.get(`${this.apiBaseUrl}/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
        withCredentials: false,
        timeout: 5000,
      });

      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 401) {
          throw new Error('Token expired or invalid');
        }
        if (error.response?.status === 403) {
          const message = error.response?.data?.detail || 'Access denied: You are not authorized to access this application';
          throw new Error(message);
        }
      }
      throw error;
    }
  }

  /**
   * Logout by calling backend endpoint
   */
  public async logout(token: string): Promise<void> {
    try {
      await axios.post(
        `${this.apiBaseUrl}/auth/logout`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
          withCredentials: false,
          timeout: 5000,
        },
      );
    } catch {
      // Silently handle logout API failures
    }
  }

  /**
   * Generate random state for OAuth security
   */
  private generateRandomState(): string {
    const array = new Uint32Array(8);
    crypto.getRandomValues(array);
    return Array.from(array, (num) => num.toString(16)).join('');
  }

  /**
   * Generate PKCE code verifier
   */
  private generateCodeVerifier(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return this.base64UrlEncode(array);
  }

  /**
   * Generate PKCE code challenge from verifier
   */
  private async generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const hash = await crypto.subtle.digest('SHA-256', data);
    return this.base64UrlEncode(new Uint8Array(hash));
  }

  /**
   * Base64 URL encode (without padding)
   */
  private base64UrlEncode(array: Uint8Array): string {
    const base64 = btoa(String.fromCharCode.apply(null, Array.from(array)));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }

  /**
   * Check if Entra ID auth is configured
   */
  public isConfigured(): boolean {
    return !!this.clientId;
  }
}

export const entraIDAuthService = new EntraIDAuthService();
